
import React from 'react';
import {
    Navigate,
    useLocation,
    useNavigate,
    useOutletContext,
    useParams,
    useRoutes
} from 'react-router-dom';
// layouts
import DashboardLayout from './layouts/dashboard';
import SimpleLayout from './layouts/simple';
// pages
import LoginPage from './pages/LoginPage';
import Page404 from './pages/Page404';
import DashboardAppPage from './pages/DashboardAppPage';
// grid pages
import DocsLayout from './layouts/DocsLayout';
import PrivacyPolicy from './pages/docs/PrivacyPolicy';
import TermsOfService from './pages/docs/TermsOfService';
import PageNotAuth from './pages/PageNotAuth';
import PermissionEditorPage from './pages/grids/PermissionEditorPage';
import ReportPreview from './pages/ReportPreview';
import UsersPage from './pages/grids/UsersPage';
import CountriesPage from './pages/grids/settings/CountriesPage';
import RolesActionsPermissionsPage from './pages/grids/RolesActionsPermissionsPage';
import RolesPage from './pages/grids/RolesPage';
import ExportCsvTasksPage from './pages/grids/settings/ExportCsvTasksPage';
import CurrenciesPage from './pages/grids/settings/CurrenciesPage';
import CurrencyRateSourcesPage from './pages/grids/settings/CurrencyRateSourcesPage';
import CurrentCurrencyRatesPage from './pages/grids/settings/CurrentCurrencyRatesPage';
import CurrencyRatesPage from './pages/grids/settings/CurrencyRatesPage';
import BanksPage from './pages/grids/settings/BanksPage';
import AgentsPage from './pages/grids/AgentsPage';
import GatewayRulesPage from './pages/grids/GatewayRulesPage';
import WalletRulesPage from './pages/grids/WalletRulesPage';
import GatewayApisPage from './pages/grids/GatewayApisPage';
import GatewaysPage from './pages/grids/GatewaysPage';
import GatewaySettingsPage from './pages/grids/GatewaySettingsPage';
import TgBotsPage from './pages/grids/TgBotsPage';
import ProxyListsPage from './pages/grids/ProxyListsPage';
import ProxiesPage from './pages/grids/ProxiesPage';
import TgSessionsPage from './pages/grids/TgSessionsPage';
import TgSessionMessagesPage from './pages/grids/TgSessionMessagesPage';
import ScraperScenariosPage from './pages/grids/ScraperScenariosPage';
import ScraperScenarioEditorPage from './pages/scraper/ScraperScenarioEditorPage';
import MerchantsPage from './pages/grids/MerchantsPage';
import MerchantTerminalsPage from './pages/grids/MerchantTerminalsPage';
import MerchantTerminalBalancesPage from './pages/grids/MerchantTerminalBalancesPage';
import WalletsPage from './pages/grids/WalletsPage';
import CommissionsPage from './pages/grids/CommissionsPage';
import PayinInstancesPage from './pages/grids/PayinInstancesPage';
import PayinEventsPage from './pages/grids/PayinEventsPage';
import PayoutInstancesPage from './pages/grids/PayoutInstancesPage';
import PayoutEventsPage from './pages/grids/PayoutEventsPage';
import AppealsPage from './pages/grids/AppealsPage';
import AppealHasCommentsPage from './pages/grids/AppealHasCommentsPage';
import AppealHasDocumentsPage from './pages/grids/AppealHasDocumentsPage';
import UrlCallbacksPage from './pages/grids/UrlCallbacksPage';
import SystemAccountsPage from './pages/grids/SystemAccountsPage';
import ExternalTransactionsPage from './pages/grids/ExternalTransactionsPage';
import GatewayTransactionsPage from './pages/grids/GatewayTransactionsPage';
import WalletTransfersPage from './pages/grids/WalletTransfersPage';
import GatewayWalletTransfersPage from './pages/grids/GatewayWalletTransfersPage';
import SystemTransactionsPage from './pages/grids/SystemTransactionsPage';
import ExpensesTransactionsPage from './pages/grids/ExpensesTransactionsPage';
import RevenueTransactionsPage from './pages/grids/RevenueTransactionsPage';

// ----------------------------------------------------------------------

/**
 * @param { ReactComponent } Component is component that will be rendered
 * @param { object } optionalProps is temporary solution for passing props to component
 * @returns {function(*): *} Component with route props
 * */
function withRouteProps(Component, optionalProps = {}) {
    function RouteWrapper(props) {
        const outletCtx = useOutletContext() || {};
        const { setHeaderActions, clearHeaderActions } = outletCtx;

        const params = useParams();
        const navigate = useNavigate();
        const location = useLocation();

        return (
            <Component
                {...props}
                {...optionalProps}
                params={params}
                navigate={navigate}
                location={location}
                setHeaderActions={setHeaderActions}
                clearHeaderActions={clearHeaderActions}
            />
        );
    }
    RouteWrapper.displayName = `withRouteProps(${Component.displayName || Component.name || 'Component'})`;
    return RouteWrapper;
}

export default function Router(props) {
    return useRoutes([
        {
            path: '/app/',
            element: <DashboardLayout />,
            children: [
                { element: <Navigate to="/app/dashboard" />, index: true },
                {
                    path: 'dashboard',
                    Component: withRouteProps(DashboardAppPage, props)
                },
                { path: 'users', Component: withRouteProps(UsersPage, props) },
                {
                    path: 'role-permissions',
                    Component: withRouteProps(PermissionEditorPage, props)
                },
                {
                    path: 'roles-actions-permissions',
                    Component: withRouteProps(
                        RolesActionsPermissionsPage,
                        props
                    )
                },

                {
                    path: 'countries',
                    Component: withRouteProps(CountriesPage, props)
                },

                {
                    path: 'roles',
                    Component: withRouteProps(RolesPage, props)
                },
                {
                    path: 'export-csv-tasks',
                    Component: withRouteProps(ExportCsvTasksPage, props)
                },
                {
                    path: 'currencies',
                    Component: withRouteProps(CurrenciesPage, props)
                },
                {
                    path: 'currency-rate-sources',
                    Component: withRouteProps(CurrencyRateSourcesPage, props)
                },
                {
                    path: 'current-currency-rates',
                    Component: withRouteProps(CurrentCurrencyRatesPage, props)
                },
                {
                    path: 'currency-rates',
                    Component: withRouteProps(CurrencyRatesPage, props)
                },
                {
                    path: 'banks',
                    Component: withRouteProps(BanksPage, props)
                },
                {
                    path: 'agents',
                    Component: withRouteProps(AgentsPage, props)
                },
                {
                    path: 'gateway-rules',
                    Component: withRouteProps(GatewayRulesPage, props)
                },
                {
                    path: 'wallet-rules',
                    Component: withRouteProps(WalletRulesPage, props)
                },
                {
                    path: 'gateway-apis',
                    Component: withRouteProps(GatewayApisPage, props)
                },
                {
                    path: 'gateways',
                    Component: withRouteProps(GatewaysPage, props)
                },
                {
                    path: 'gateway-settings',
                    Component: withRouteProps(GatewaySettingsPage, props)
                },
                {
                    path: 'tg-bots',
                    Component: withRouteProps(TgBotsPage, props)
                },
                {
                    path: 'tg-sessions',
                    Component: withRouteProps(TgSessionsPage, props)
                },
                {
                    path: 'tg-session-messages',
                    Component: withRouteProps(TgSessionMessagesPage, props)
                },
                {
                    path: 'scraper-scenarios',
                    Component: withRouteProps(ScraperScenariosPage, props)
                },
                // The first :id route under /app: a scenario's code needs a page
                // of its own, not a dialog. `new` creates one.
                {
                    path: 'scraper-scenarios/:id',
                    Component: withRouteProps(ScraperScenarioEditorPage, props)
                },
                {
                    path: 'proxy-lists',
                    Component: withRouteProps(ProxyListsPage, props)
                },
                {
                    path: 'proxies',
                    Component: withRouteProps(ProxiesPage, props)
                },
                {
                    path: 'merchants',
                    Component: withRouteProps(MerchantsPage, props)
                },
                {
                    path: 'merchant-terminals',
                    Component: withRouteProps(MerchantTerminalsPage, props)
                },
                {
                    path: 'merchant-terminal-balances',
                    Component: withRouteProps(MerchantTerminalBalancesPage, props)
                },
                {
                    path: 'wallets',
                    Component: withRouteProps(WalletsPage, props)
                },
                {
                    path: 'commissions',
                    Component: withRouteProps(CommissionsPage, props)
                },
                {
                    path: 'payin-instances',
                    Component: withRouteProps(PayinInstancesPage, props)
                },
                {
                    path: 'payin-events',
                    Component: withRouteProps(PayinEventsPage, props)
                },
                {
                    path: 'payout-instances',
                    Component: withRouteProps(PayoutInstancesPage, props)
                },
                {
                    path: 'payout-events',
                    Component: withRouteProps(PayoutEventsPage, props)
                },
                {
                    path: 'appeals',
                    Component: withRouteProps(AppealsPage, props)
                },
                {
                    path: 'appeal-comments',
                    Component: withRouteProps(AppealHasCommentsPage, props)
                },
                {
                    path: 'appeal-documents',
                    Component: withRouteProps(AppealHasDocumentsPage, props)
                },
                {
                    path: 'url-callbacks',
                    Component: withRouteProps(UrlCallbacksPage, props)
                },
                {
                    path: 'system-accounts',
                    Component: withRouteProps(SystemAccountsPage, props)
                },
                {
                    path: 'external-transactions',
                    Component: withRouteProps(ExternalTransactionsPage, props)
                },
                {
                    path: 'gateway-transactions',
                    Component: withRouteProps(GatewayTransactionsPage, props)
                },
                {
                    path: 'wallet-transfers',
                    Component: withRouteProps(WalletTransfersPage, props)
                },
                {
                    path: 'gateway-wallet-transfers',
                    Component: withRouteProps(GatewayWalletTransfersPage, props)
                },
                {
                    path: 'system-transactions',
                    Component: withRouteProps(SystemTransactionsPage, props)
                },
                {
                    path: 'expenses-transactions',
                    Component: withRouteProps(ExpensesTransactionsPage, props)
                },
                {
                    path: 'revenue-transactions',
                    Component: withRouteProps(RevenueTransactionsPage, props)
                }

            ]
        },
        {
            path: 'login',
            Component: withRouteProps(LoginPage, props)
        },
        {
            path: '/reportTypes/:reportId',
            Component: withRouteProps(ReportPreview, props)
        },
        {
            path: '/doc/api-static.html',
            element: () => {
                window.location.href = '/doc/api-static.html';
                return null;
            }
        },
        {
            element: <SimpleLayout />,
            children: [
                { element: <Navigate to="/app/dashboard" />, index: true },
                {
                    path: '/not-auth',
                    Component: withRouteProps(PageNotAuth, props)
                },
                { path: '404', Component: withRouteProps(Page404, props) },
                { path: '*', element: <Navigate to="/404" /> }
            ]
        },
        {
            path: 'docs',
            element: <DocsLayout />,
            children: [
                { path: '', element: <Navigate to="/docs/privacy" /> },
                {
                    path: 'privacy',
                    Component: withRouteProps(PrivacyPolicy, props)
                },
                {
                    path: 'tos',
                    Component: withRouteProps(TermsOfService, props)
                },
                { path: '*', element: <Navigate to="/docs/privacy" /> }
            ]
        },
        {
            path: '*',
            element: <Navigate to="/404" replace />
        }
    ]);
}

